/**
 * Platform-detection helpers.
 *
 * React Native's `Platform.OS === 'web'` answers "are we running in
 * react-native-web?", but doesn't distinguish iOS Safari from Android
 * Chrome inside the browser. A few visual fixes need that distinction —
 * iOS Safari renders the active-game landscape layout with measurably
 * less usable horizontal space than Android Chrome at the same logical
 * viewport size, so map polygons get covered by the corner overlay
 * chips unless we reserve extra padding.
 */

import { Platform } from 'react-native'

/**
 * True iff running in a browser AND the user agent identifies as iOS.
 * Returns false on native iOS (where Platform.OS === 'ios' but the
 * caller is not in the web rendering pipeline this helper covers).
 */
export function isIOSWeb(): boolean {
  if (Platform.OS !== 'web') return false
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  // Catch iPhone, iPod, iPad (modern + legacy). iPadOS 13+ reports
  // 'Macintosh' with touch points; treat that as iOS too.
  if (/iPad|iPhone|iPod/.test(ua)) return true
  if (ua.includes('Macintosh') && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return true
  }
  return false
}

/**
 * True when the web UI is being driven by a touch-first pointer. This keeps
 * touch-only interaction fixes away from desktop browsers, including desktop
 * chat text selection.
 */
export function isCoarsePointerWeb(): boolean {
  if (Platform.OS !== 'web') return false
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches
}
